import { Link } from '@tanstack/react-router'
import { useDays, useTasks } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { clock, dayOf, markDay, nightOf, nowIso, recapHeadline } from '@/lib/day'
import { fmtMinutes, todayLocal } from '@/lib/time'
import { Button, PageTitle } from '@/components/ui'
import { Phrases } from '@/components/Phrases'

/**
 * The evening recap at /recap, opened by the recap push: how the day went in
 * one line, what got done (the only counting allowed), what is left by name
 * with a nudge rather than a number, and "Lights out" to close the day.
 */
export const RecapView = () => {
  const tasks = useTasks() ?? []
  const days = useDays()
  const now = useNow(30_000)
  const today = todayLocal()
  const summary = dayOf(tasks, today)
  const head = recapHeadline(
    summary.done.length,
    summary.left.map((t) => t.spec.title),
  )
  const minutes = summary.done.reduce((s, t) => s + (t.status.actual_minutes ?? 0), 0)
  const night = days.find((d) => d.spec.date === nightOf(now))
  const left = summary.left.slice(0, 3)
  return (
    <div data-testid="recap">
      <PageTitle
        eyebrow={new Date(now).toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
      >
        {head.title}
      </PageTitle>
      <p className="text-lg leading-snug mb-5" data-testid="recap-line">
        {head.body}
      </p>
      {left.length > 0 && (
        <section className="mb-5">
          <div className="text-[13px] font-semibold text-muted mb-1.5">Still on for today</div>
          <ul className="group-list" data-testid="recap-left">
            {left.map((t) => (
              <li key={t.id}>
                <Link
                  to="/tasks/$id"
                  params={{ id: t.id }}
                  className="flex items-baseline justify-between gap-3 px-3.5 py-2.5 min-h-11 text-[15px]"
                >
                  <span className="min-w-0">{t.spec.title}</span>
                  {t.spec.estimate_minutes ? (
                    <span className="text-xs text-muted tabular-nums shrink-0">
                      {fmtMinutes(t.spec.estimate_minutes)}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
          {summary.left.length > left.length && (
            <Link to="/" className="inline-block mt-2 text-xs text-muted hover:text-fg">
              The rest is on Today
            </Link>
          )}
        </section>
      )}
      <section className="mb-5">
        <Phrases
          as="div"
          className="text-[13px] font-semibold text-muted mb-1.5"
          parts={[
            'Done today',
            !!minutes && <span className="font-normal">{fmtMinutes(minutes)} tracked</span>,
          ]}
        />
        {summary.done.length === 0 ? (
          <p className="text-sm text-faint">Nothing ticked off yet.</p>
        ) : (
          <ul className="group-list" data-testid="recap-done">
            {summary.done.map((t) => (
              <li key={t.id} className="flex items-baseline justify-between gap-3 text-sm px-3.5 py-2.5">
                <span className="text-fg/90">{t.spec.title}</span>
                <span className="text-xs text-faint tabular-nums shrink-0">
                  {clock(t.status.completed_at!)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card px-3.5 py-3 mb-5 flex items-center justify-between gap-3">
        {night?.spec.slept_at ? (
          <div className="text-sm">Lights out at {clock(night.spec.slept_at)}. Sleep well.</div>
        ) : (
          <>
            <div className="text-sm text-muted">Going to bed? Lights out closes the day.</div>
            <Button
              variant="primary"
              className="shrink-0 whitespace-nowrap"
              onClick={() => void markDay(nightOf(Date.now()), { slept_at: nowIso() })}
              data-testid="recap-sleep"
            >
              Lights out
            </Button>
          </>
        )}
      </section>
      <p className="flex gap-4">
        <Link to="/" className="text-xs text-faint hover:text-fg">
          Today
        </Link>
        <Link to="/log" className="text-xs text-faint hover:text-fg">
          The Log
        </Link>
      </p>
    </div>
  )
}
